import AddIcon from "@mui/icons-material/Add";
import CleaningServicesOutlined from "@mui/icons-material/CleaningServicesOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import PrintIcon from "@mui/icons-material/Print";
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonCustom } from "../../components/confirmationArea/ConfirmationArea.styled";
import { PageCard } from "../../components/pageCard/PageCard";
import { CustomMenuItem } from "../../components/table/CustomMenuItem/CustomMenuItem";
import { CustomNoContentTableBody } from "../../components/table/CustomNoContentTableBody/CustomNoContentTableBody";
import { CustomTableCell } from "../../components/table/CustomTableCell/CustomTableCell";
import CustomTablePagination from "../../components/table/CustomTablePagination/CustomTablePagination";
import CustomTableSortLabel from "../../components/table/CustomTableSortLabel/CustomTableSortLabel";
import { FilterButtonGroup } from "../../components/table/FilterButtonGroup/FilterButtonGroup";
import { GridFilterArea } from "../../components/table/GridFilterArea/GridFilterArea";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { ICollectorPlant } from "../../interfaces/ICollectorPlant";
import { IListAllParams } from "../../interfaces/IListAllParams";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";
import { openBase64Pdf } from "../../utils/downloadPdf";
import { getUtcOffset } from "../../utils/timezone";

const fields = [
  { field: "number", label: "Número" },
  { field: "name", label: "Nome" },
  { field: "genealogy", label: "Genealogia" },
];

const STORAGE_KEY = "@ORQUIDEAS:collectorPlantsFilters";

const getInitialState = (key: string, defaultValue: any) => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    const parsed = JSON.parse(saved);
    return parsed[key] !== undefined ? parsed[key] : defaultValue;
  }
  return defaultValue;
};

export const CollectorPlants: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const { exhibitionId, collectorId } = useParams<{
    exhibitionId: string;
    collectorId: string;
  }>();

  const toastr = useToastr();
  const timeout = useRef<any>(null);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const [plants, setPlants] = useState<ICollectorPlant[]>([]);
  const [totalRows, setTotalRows] = useState(0);

  const [perPage, setPerPage] = useState<number>(() =>
    getInitialState("perPage", 10),
  );
  const [currentPage, setCurrentPage] = useState<number>(() =>
    getInitialState("currentPage", 0),
  );
  const [orderField, setOrderField] = useState<string>(() =>
    getInitialState("orderField", "number"),
  );
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">(() =>
    getInitialState("orderDirection", "asc"),
  );
  const [loading, setLoading] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingCards, setLoadingCards] = useState(false);

  const [gridFilterAreaOpen, setGridFilterAreaOpen] = useState(() =>
    getInitialState("gridFilterAreaOpen", false),
  );
  const [filterField, setFilterField] = useState<string>(() =>
    getInitialState("filterField", "name"),
  );
  const [filterPrecision, setFilterPrecision] = useState<string>(() =>
    getInitialState("filterPrecision", "containing"),
  );
  const [filterValue, setFilterValue] = useState<string>(() =>
    getInitialState("filterValue", ""),
  );
  const [delay, setDelay] = useState(0);

  const handleToCollectorPlant = useCallback(
    (id: string | null) => {
      const path = id ? `/collector-plant/${id}` : "/collector-plant";
      navigate(
        `${path}?exhibitionId=${exhibitionId}&collectorId=${collectorId}`,
      );
    },
    [navigate, exhibitionId, collectorId],
  );

  const handleListAll = useCallback(
    async ({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    }: IListAllParams) => {
      let url = `/collector/plants/exhibition/${exhibitionId}/collector/${collectorId}?perPage=${perPage}&currentPage=${
        currentPage + 1
      }&orderBy=${orderField}&orderDirection=${orderDirection}`;

      if (field && value && precision) {
        url += `&filterField=${field}&filterValue=${value}&precision=${precision}`;
      }

      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        setLoading(true);
        await api
          .get(url)
          .then(response => {
            const responseData = response.data;
            if (responseData.data) {
              setPlants(responseData.data);
              setTotalRows(responseData.totalRows || 0);
            } else {
              setPlants([]);
              setTotalRows(0);
            }
          })
          .catch(error => {
            if (error.response?.status === 401) {
              signOut();
              navigate("/");
            }
            toastr.error(
              error?.response?.data?.message ||
                "Erro ao listar plantas do colecionador",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }, delay || 0);
    },
    [exhibitionId, collectorId, signOut, navigate, toastr],
  );

  const handlePagination = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: "asc" | "desc",
  ) => {
    setPerPage(perPage);
    setCurrentPage(currentPage);
    setOrderField(orderField);
    setOrderDirection(orderDirection);
    setDelay(0);
  };

  useEffect(() => {
    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field: filterField,
      value: filterValue,
      precision: filterPrecision,
      delay,
    });
  }, [
    filterField,
    filterPrecision,
    filterValue,
    delay,
    currentPage,
    orderDirection,
    orderField,
    perPage,
    handleListAll,
  ]);

  const handleTextFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
    setTotalRows(0);
    setCurrentPage(0);
    setDelay(500);
  };

  const saveFiltersToStorage = useCallback(() => {
    const filters = {
      filterField,
      filterPrecision,
      filterValue,
      gridFilterAreaOpen,
      currentPage,
      perPage,
      orderField,
      orderDirection,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    orderField,
    orderDirection,
  ]);

  useEffect(() => {
    saveFiltersToStorage();
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    saveFiltersToStorage,
  ]);

  const handlePrintReport = async () => {
    const newWindow = window.open("", "_blank");
    if (newWindow) {
      newWindow.document.write("<p>Gerando relatório... Aguarde.</p>");
    }
    setLoadingReport(true);
    try {
      const response = await api.get(
        `/collector/plants/report/exhibition/${exhibitionId}/collector/${collectorId}/open`,
        { params: { utcOffset: getUtcOffset() } },
      );
      const files: { filename: string; base64: string }[] = response.data;
      if (!files?.length || !files[0]?.base64) {
        toastr.error("Nenhum relatório encontrado para gerar");
        if (newWindow) newWindow.close();
        return;
      }
      const { base64, filename } = files[0];
      openBase64Pdf(base64, filename || "relatorio.pdf", newWindow);
    } catch (error: any) {
      if (newWindow) newWindow.close();
      toastr.error(error?.response?.data?.message || "Erro ao gerar relatório");
    } finally {
      setLoadingReport(false);
    }
  };

  const handleGenerateCards = async (plantIdsArray: string[]) => {
    const newWindow = window.open("", "_blank");
    if (newWindow) {
      newWindow.document.write("<p>Gerando cartões... Aguarde.</p>");
    }
    setLoadingCards(true);
    try {
      const response = await api.post(`/collector/plants/collector/${collectorId}/cards/generate`, {
        plantIds: plantIdsArray,
      });
      const { base64, filename } = response.data;
      if (!base64) {
        toastr.error("Nenhum cartão encontrado para gerar");
        if (newWindow) newWindow.close();
        return;
      }
      openBase64Pdf(base64, filename || "cartoes.pdf", newWindow);
    } catch (error: any) {
      if (newWindow) newWindow.close();
      toastr.error(error?.response?.data?.message || "Erro ao gerar cartões");
    } finally {
      setLoadingCards(false);
    }
  };

  const handleGenerateAllCards = async () => {
    const plantIdsArray = plants.map((p: any) => p.id).filter(Boolean);
    
    if (plantIdsArray.length === 0) {
      toastr.error("Nenhuma planta encontrada para gerar cartões");
      return;
    }
    
    await handleGenerateCards(plantIdsArray);
  };

  const handleClearFilters = () => {
    setFilterField("name");
    setFilterPrecision("containing");
    setFilterValue("");
    setCurrentPage(0);
    setPerPage(10);
    setOrderField("number");
    setOrderDirection("asc");
    setDelay(0);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      <TitleContainer>
        <h1>Minhas Plantas</h1>
      </TitleContainer>
      <PageCard>
        <FilterButtonGroup>
          <div>
            <Tooltip title="Filtros" placement="top">
              <IconButton
                onClick={() => setGridFilterAreaOpen(!gridFilterAreaOpen)}
                size="small"
                color="primary"
                style={{ alignItems: "flex-end" }}
              >
                {gridFilterAreaOpen ? (
                  <FilterAltOffOutlinedIcon />
                ) : (
                  <FilterAltOutlinedIcon />
                )}
              </IconButton>
            </Tooltip>

            {gridFilterAreaOpen && (
              <Tooltip title="Limpar Filtros" placement="top">
                <IconButton
                  onClick={handleClearFilters}
                  size="small"
                  color="primary"
                  style={{ alignItems: "flex-end" }}
                >
                  <CleaningServicesOutlined />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrintReport}
              disabled={loadingReport || loadingCards}
              sx={{ height: "30px" }}
            >
              Imprimir Relatório
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handleGenerateAllCards}
              disabled={loadingReport || loadingCards || plants.length === 0}
              sx={{ height: "30px" }}
            >
              Gerar Cartões
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleToCollectorPlant(null)}
              sx={{ height: "30px" }}
            >
              Adicionar
            </Button>
          </div>
        </FilterButtonGroup>

        {gridFilterAreaOpen && (
          <GridFilterArea container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="select-filter-label">Filtrar por</InputLabel>
                <Select
                  labelId="select-filter-label"
                  label="Filtrar Por"
                  value={filterField}
                  onChange={e => {
                    setFilterField(String(e.target.value));
                  }}
                >
                  {fields.map(filter => (
                    <MenuItem key={filter.field} value={filter.field}>
                      {filter.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="select-precision-label">Precisão</InputLabel>
                <Select
                  labelId="select-precision-label"
                  label="Precisão"
                  value={filterPrecision}
                  onChange={e => {
                    setFilterPrecision(String(e.target.value));
                  }}
                >
                  <MenuItem key={"equal"} value={"equal"}>
                    Igual
                  </MenuItem>
                  <MenuItem key={"containing"} value={"containing"}>
                    Contendo
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Valor a filtrar"
                placeholder="Valor a filtrar"
                value={filterValue}
                onChange={handleTextFilterChange}
                size="small"
              />
            </Grid>
          </GridFilterArea>
        )}

        <TableContainer sx={{ minHeight: "250px" }}>
          <Table size="small" sx={{ padding: "16px" }}>
            <TableHead>
              <TableRow
                sx={{
                  "&:last-child td, &:last-child th": {
                    borderBottom: 1,
                    borderColor: "#E5E5E5",
                  },
                }}
              >
                <TableCell size="small" width={60}>
                  Ações
                </TableCell>
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="number"
                  label="Número"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="name"
                  label="Nome"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="genealogy"
                  label="Genealogia"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {plants.length > 0 &&
                plants.map(plant => (
                  <TableRow
                    key={plant.id}
                    sx={{
                      "&:last-child td, &:last-child th": {
                        border: 0,
                      },
                    }}
                  >
                    <CustomMenuItem
                      id={plant.id ? String(plant.id) : ""}
                      handleToView={handleToCollectorPlant}
                      handleToPrintOut={id => handleGenerateCards([id])}
                    />

                    <CustomTableCell size="small" width={100}>
                      {plant.number}
                    </CustomTableCell>

                    <CustomTableCell
                      size="small"
                      title={plant.name}
                      width={400}
                    >
                      {plant.name}
                    </CustomTableCell>

                    <CustomTableCell
                      size="small"
                      title={plant.genealogy || ""}
                      width={350}
                    >
                      {plant.genealogy || "-"}
                    </CustomTableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {plants.length === 0 && (
            <CustomNoContentTableBody description="Nenhuma planta encontrada" />
          )}
        </TableContainer>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "16px",
          }}
        >
          <ButtonCustom
            variant="contained"
            color="inherit"
            onClick={() => navigate(`/collector/${collectorId}`)}
          >
            Voltar
          </ButtonCustom>
          <CustomTablePagination
            totalRows={totalRows}
            currentPage={currentPage}
            perPage={perPage}
            handleRequest={handlePagination}
            setPerPage={setPerPage}
            setCurrentPage={setCurrentPage}
            orderField={orderField}
            orderDirection={orderDirection}
          />
        </div>
        {(loading || loadingReport || loadingCards) && <BackdropCustom />}
      </PageCard>
    </>
  );
};
